import mitt from 'mitt';


const log = process.env.NODE_ENV === 'development'
	? (...args) => window.console.log('%cwidget%c', 'color: red', 'color: initial', ...args)
	: () => {};


const WIDGET_OPEN_WIDTH = 365;
const WIDGET_OPEN_HEIGHT = 525;
const WIDGET_MINIMIZED_WIDTH = 54;
const WIDGET_MINIMIZED_HEIGHT = 54;
const WIDGET_MARGIN = 16;


window.BarantumLivechat = window.BarantumLivechat || { _: [] };
const config = {};
let widget;
let iframe;
let hookQueue = [];
let ready = false;
let smallScreen = false;
let scrollPosition;
let widget_height;

export const validCallbacks = [
	'chat-maximized',
	'chat-minimized',
	'chat-started',
	'chat-ended',
	'pre-chat-form-submit',
	'offline-form-submit',
	'show-widget',
	'hide-widget',
	'assign-agent',
	'agent-status-change',
	'queue-position-change',
	'no-agent-online',
	'SHOW_IMAGE_PREVIEW'
];

const callbacks = mitt();

function registerCallback(eventName, fn) {
	if (validCallbacks.indexOf(eventName) === -1) {
		return false;
	}

	return callbacks.on(eventName, fn);
}

function emitCallback(eventName, data) {
	if (typeof data !== 'undefined') {
		callbacks.emit(eventName, data);
	} else {
		callbacks.emit(eventName);
	}
}

// hooks
function callHook(action, params) {
	if (!ready) {
		return hookQueue.push([action, params]);
	}
	const data = {
		src: 'rocketchat',
		fn: action,
		args: params,
	};
	iframe.contentWindow.postMessage(data, '*');
}

const updateWidgetStyle = (isOpened) => {
	if (smallScreen && isOpened) {
		scrollPosition = document.documentElement.scrollTop;
		document.body.classList.add('rc-livechat-mobile-full-screen');
	} else {
		document.body.classList.remove('rc-livechat-mobile-full-screen');
		if (smallScreen) {
			document.documentElement.scrollTop = scrollPosition;
		}
	}

	if (isOpened) {
		widget.style.left = smallScreen ? '0' : 'auto';

		/**
		 * If we use widget.style.height = smallScreen ? '100vh' : ...
		 * In above case some browser's viewport height is not rendered correctly
		 * so, as 100vh will resolve to 100% of the current viewport height,
		 * so fixed it to 100% avoiding problem for some browsers. Similar resolution
		 * for widget.style.width
		 */

		widget.style.height = smallScreen ? '100%' : `${ WIDGET_MARGIN + widget_height + WIDGET_MARGIN + WIDGET_MINIMIZED_HEIGHT }px`;
		widget.style.width = smallScreen ? '100%' : `${ WIDGET_MARGIN + WIDGET_OPEN_WIDTH + WIDGET_MARGIN }px`;
	} else {
		widget.style.left = 'auto';
		widget.style.width = `${ WIDGET_MARGIN + WIDGET_MINIMIZED_WIDTH + WIDGET_MARGIN }px`;
		widget.style.height = `${ WIDGET_MARGIN + WIDGET_MINIMIZED_HEIGHT + WIDGET_MARGIN }px`;
	}
};

const createWidget = (url,serverUrl) => {
	widget = document.createElement('div');
	widget.className = 'barantum-livechat-widget';
	widget.style.position = 'fixed';
	widget.style.width = `${ WIDGET_MARGIN + WIDGET_MINIMIZED_WIDTH + WIDGET_MARGIN }px`;
	widget.style.height = `${ WIDGET_MARGIN + WIDGET_MINIMIZED_HEIGHT + WIDGET_MARGIN }px`;
	widget.style.maxHeight = '100vh';
	widget.style.bottom = '0';
	widget.style.right = '0';
	widget.style.zIndex = '12345';
	widget.dataset.state = 'closed';

	const container = document.createElement('div');
	container.className = 'barantum-livechat-container';
	container.style.width = '100%';
	container.style.height = '100%';

	iframe = document.createElement('iframe');
	iframe.id = 'barantum-livechat-iframe';
	iframe.allowTransparency = 'true';
	iframe.src = url+"?serverUrl="+serverUrl;
	iframe.style.width = '100%';
	iframe.style.height = '100%';
	iframe.style.border = 'none';
	iframe.style.backgroundColor = 'transparent';

	container.appendChild(iframe);
	widget.appendChild(container);
	document.body.appendChild(widget);

	const handleMediaQueryTest = ({ matches }) => {
		if (!widget) {
			return;
		}

		smallScreen = matches;
		updateWidgetStyle(widget.dataset.state === 'opened');
		callHook('setExpanded', smallScreen);
	};

	const mediaQueryList = window.matchMedia('screen and (max-device-width: 480px)');
	mediaQueryList.addListener(handleMediaQueryTest);
	handleMediaQueryTest(mediaQueryList);
};

const openWidget = () => {
	if (widget.dataset.state === 'opened') {
		return;
	}

	widget_height = WIDGET_OPEN_HEIGHT;
	updateWidgetStyle(true);
	widget.dataset.state = 'opened';
	iframe.focus();
	emitCallback('chat-maximized');
};

const resizeWidget = (height) => {
	widget_height = height;
	updateWidgetStyle(true);
	widget.dataset.state = 'triggered';
};

function closeWidget() {
	if (widget.dataset.state === 'closed') {
		return;
	}

	updateWidgetStyle(false);
	widget.dataset.state = 'closed';
	emitCallback('chat-minimized');
}

const api = {
	popup: null,

	ready() {
		ready = true;
		if (hookQueue.length > 0) {
			hookQueue.forEach(function(hookParams) {
				callHook.apply(this, hookParams);
			});
			hookQueue = [];
		}
	},

	minimizeWindow() {
		closeWidget();
	},

	restoreWindow() {
		if (api.popup && api.popup.closed !== true) {
			api.popup.close();
			api.popup = null;
		}
		openWidget();
	},

	openPopout(x) {
		closeWidget();
		const serverParam = `serverUrl=${encodeURIComponent(config.serverUrl)}`;
		const visitorParam = `token=${encodeURIComponent(x.token)}`;
		const joiner = config.url.includes('?') ? '&' : '?';
		const popupUrl = `${config.url}${joiner}mode=popout&${serverParam}&${visitorParam}`;
		api.popup = window.open(
			popupUrl,
			'livechat-popout',
			`width=${WIDGET_OPEN_WIDTH}, height=${widget_height}, toolbars=no`
		);
		api.popup.focus()
	},

	openWidget() {
		openWidget();
	},

	resizeWidget(height) {
		resizeWidget(height);
	},

	removeWidget() {
		document.body.removeChild(widget);
	},

	callback(eventName, data) {
		emitCallback(eventName, data);
	},

	showWidget() {
		iframe.style.display = 'initial';
		emitCallback('show-widget');
	},

	hideWidget() {
		iframe.style.display = 'none';
		emitCallback('hide-widget');
	},

	resetDocumentStyle() {
		document.body.classList.remove('rc-livechat-mobile-full-screen');
	},

	setFullScreenDocumentMobile() {
		smallScreen && document.body.classList.add('rc-livechat-mobile-full-screen');
	},
};

function pageVisited(change) {
	callHook('pageVisited', {
		change,
		location: JSON.parse(JSON.stringify(document.location)),
		title: document.title,
	});
}

function setCustomField(key, value, overwrite) {
	if (typeof overwrite === 'undefined') {
		overwrite = true;
	}
	callHook('setCustomField', [key, value, overwrite]);
}

function setCustomFields(x) {
	callHook('setCustomFields', {setCustomFields:x});
}

function sanitazedInput(x){
	callHook('sanitazedInput', x);
}

function setTheme(theme) {
	callHook('setTheme', theme);
}

function setDepartment(department) {
	callHook('setDepartment', department);
}

function setBusinessUnit(businessUnit) {
	callHook('setBusinessUnit', businessUnit);
}

function clearBusinessUnit() {
	callHook('clearBusinessUnit');
}

function closeChat(x) {
	callHook('closeChat',x);
}

function setGuestToken(token) {
	callHook('setGuestToken', token);
}

function setGuestName(name) {
	callHook('setGuestName', name);
}

function setGuestEmail(email) {
	callHook('setGuestEmail', email);
}

function registerGuest(guest) {
	callHook('registerGuest', guest);
}

function clearDepartment() {
	callHook('clearDepartment');
}

function setAgent(agent) {
	callHook('setAgent', agent);
}

function setLanguage(language) {
	callHook('setLanguage', language);
}

function showWidget() {
	callHook('showWidget');
}

function hideWidget() {
	callHook('hideWidget');
}

function maximizeWidget() {
	callHook('maximizeWidget');
}

function minimizeWidget() {
	callHook('minimizeWidget');
}

function initialize(params) {
	for (const method in params) {
		if (!params.hasOwnProperty(method)) {
			continue;
		}

		switch (method) {
			case 'customField':
				const { key, value, overwrite } = params[method];
				setCustomField(key, value, overwrite);
				continue;
			case 'setCustomFields':
				if (!Array.isArray(params[method])) {
					console.log('Error: Invalid parameters. Value must be an array of objects');
					continue;
				}
				params[method].forEach((data) => {
					const { key, value, overwrite } = data;
					setCustomField(key, value, overwrite);
				});
				continue;
			case 'theme':
				setTheme(params[method]);
				continue;
			case 'department':
				setDepartment(params[method]);
				continue;
			case 'businessUnit': {
				setBusinessUnit(params[method]);
				continue;
			}
			case 'guestToken':
				setGuestToken(params[method]);
				continue;
			case 'guestName':
				setGuestName(params[method]);
				continue;
			case 'guestEmail':
				setGuestEmail(params[method]);
				continue;
			case 'registerGuest':
				registerGuest(params[method]);
				continue;
			case 'language':
				setLanguage(params[method]);
				continue;
			case 'agent':
				setAgent(params[method]);
				continue;
			default:
				continue;
		}
	}
}

const currentPage = {
	href: null,
	title: null,
};

const showImagePreview = (url) => {
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0,0,0,0.8)';
    modal.style.display = 'flex';
    modal.style.justifyContent = 'center';
    modal.style.alignItems = 'center';
    modal.style.zIndex = '999999';

    const img = document.createElement('img');
    img.src = url;
    img.style.maxWidth = '90%';
    img.style.maxHeight = '90%';
    modal.appendChild(img);

    const closeButton = document.createElement('button');
    closeButton.innerText = 'Close';
    closeButton.style.position = 'absolute';
    closeButton.style.top = '20px';
    closeButton.style.right = '20px';
    closeButton.style.backgroundColor = '#ff4d4d';
    closeButton.style.color = '#fff';
    closeButton.style.border = 'none';
    closeButton.style.padding = '12px 20px';
    closeButton.style.borderRadius = '5px';
    closeButton.style.fontSize = '16px';
    closeButton.style.cursor = 'pointer';
    closeButton.style.transition = 'background-color 0.3s ease';
    closeButton.style.zIndex = '1000';
    closeButton.onclick = (event) => {
        event.stopPropagation(); 
        document.body.removeChild(modal);
    };

    const downloadButton = document.createElement('a');
    downloadButton.innerText = 'Download';
    downloadButton.style.position = 'absolute';
    downloadButton.style.bottom = '20px';
    downloadButton.style.right = '20px';
    downloadButton.style.backgroundColor = '#4CAF50';
    downloadButton.style.color = '#fff';
    downloadButton.style.border = 'none';
    downloadButton.style.padding = '12px 20px';
    downloadButton.style.borderRadius = '5px';
    downloadButton.style.fontSize = '16px';
    downloadButton.style.cursor = 'pointer';
    downloadButton.style.transition = 'background-color 0.3s ease';
    downloadButton.style.zIndex = '1000';
    downloadButton.setAttribute("target","_blank");
    downloadButton.setAttribute("href",url);
    modal.appendChild(closeButton);
    modal.appendChild(downloadButton);
    document.body.appendChild(modal);
};






const attachMessageListener = () => {
	window.addEventListener('message', (msg) => {
		if (typeof msg.data === 'object' && msg.data.src !== undefined && msg.data.src === 'rocketchat') {
			if (api[msg.data.fn] !== undefined && typeof api[msg.data.fn] === 'function') {
				const args = [].concat(msg.data.args || []);
				log(`api.${ msg.data.fn }`, ...args);
				api[msg.data.fn].apply(null, args);
			}
		}
	}, false);
};

const trackNavigation = () => {
	setInterval(() => {
		if (document.location.href !== currentPage.href) {
			pageVisited('url');
			currentPage.href = document.location.href;
		}

		if (document.title !== currentPage.title) {
			pageVisited('title');
			currentPage.title = document.title;
		}
	}, 800);
};

const init = (url,serverUrl) => {
	if (!url) {
		return;
	}

	config.url = url;
	config.serverUrl = serverUrl;
	createWidget(url,serverUrl);
	attachMessageListener();
	trackNavigation();
	window.addEventListener('message', function(event) {
        if (event.data.src === 'rocketchat') {
            if (event.data.fn === 'SHOW_IMAGE_PREVIEW') {
                const imageUrl = event.data.args[0];
                showImagePreview(event.data.args[0]);
            }
        }
    });
};

if (typeof window.initRocket !== 'undefined') {
	console.warn('initRocket is now deprecated. Please update the livechat code.');
	init(window.initRocket[0]);
}

if (typeof window.BarantumLivechat.url !== 'undefined' && typeof window.BarantumLivechat.serverUrl !== 'undefined') {
	init(window.BarantumLivechat.url,window.BarantumLivechat.serverUrl);
}

const queue = window.BarantumLivechat._;

window.BarantumLivechat._.push = function(c) {
	c.call(window.BarantumLivechat.livechat);
};
window.BarantumLivechat = window.BarantumLivechat._.push;
// exports
window.BarantumLivechat.livechat = {
	// methods
	pageVisited,
	setCustomField,
	setCustomFields,
	initialize,
	setTheme,
	setDepartment,
	clearDepartment,
	setGuestToken,
	setGuestName,
	setGuestEmail,
	setAgent,
	registerGuest,
	setLanguage,
	showWidget,
	hideWidget,
	maximizeWidget,
	minimizeWidget,
	setBusinessUnit,
	clearBusinessUnit,
	closeChat,
	sanitazedInput,

	// callbacks
	onChatMaximized(fn) { registerCallback('chat-maximized', fn); },
	onChatMinimized(fn) { registerCallback('chat-minimized', fn); },
	onChatStarted(fn) { registerCallback('chat-started', fn); },
	onChatEnded(fn) { callHook('closeChat',1);registerCallback('chat-ended', fn); },
	onPrechatFormSubmit(fn) { registerCallback('pre-chat-form-submit', fn); },
	onOfflineFormSubmit(fn) { registerCallback('offline-form-submit', fn); },
	onWidgetShown(fn) { registerCallback('show-widget', fn); },
	onWidgetHidden(fn) { registerCallback('hide-widget', fn); },
	onAssignAgent(fn) { registerCallback('assign-agent', fn); },
	onAgentStatusChange(fn) { registerCallback('agent-status-change', fn); },
	onQueuePositionChange(fn) { registerCallback('queue-position-change', fn); },
	onServiceOffline(fn) { registerCallback('no-agent-online', fn); },
	onSHOW_IMAGE_PREVIEW(fn) { registerCallback('SHOW_IMAGE_PREVIEW', fn); },
};

// proccess queue
queue.forEach((c) => {
	c.call(window.BarantumLivechat.livechat);
});
